import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useTranslation } from "react-i18next";
import { ErrorState, LoadingSkeleton } from "@/components/states";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { MediaAttachButton } from "@/components/MediaAttachButton";
import { parseProgram, saveProgram, type ParsedProgramDoc } from "@/lib/curriculum.functions";
import {
  Upload, Loader2, Plus, Trash2, ChevronRight, GraduationCap, Info,
} from "lucide-react";
import { PageHeader } from "@/components/PageHeader";

export const Route = createFileRoute("/_app/programs/")({ component: ProgramsPage });

export type TopicRow = {
  id: string;
  curriculum_id: string;
  unit_id: string | null;
  title: string;
  status: string;
  knowledge_score: number;
  exam_date: string | null;
  position: number;
  subtopics: string[] | null;
  difficulty: string;
  estimated_minutes: number;
};

function useCurricula() {
  return useQuery({
    queryKey: ["curricula"],
    queryFn: async () => {
      const { data: u } = await supabase.auth.getUser();
      if (!u.user) return { curricula: [], topics: [] as TopicRow[] };
      const [{ data: curricula }, { data: topics }] = await Promise.all([
        supabase.from("curricula").select("id,subject_name,emoji,teacher,school_year").order("created_at"),
        supabase.from("curriculum_topics").select("id,curriculum_id,status,knowledge_score"),
      ]);
      return { curricula: curricula ?? [], topics: (topics ?? []) as unknown as TopicRow[] };
    },
  });
}

export function progressFor(topics: { status: string; knowledge_score: number }[]) {
  if (!topics.length) return 0;
  const total = topics.reduce(
    (acc, t) => acc + (t.status === "done" ? 100 : t.status === "in_progress" ? Math.max(40, t.knowledge_score) : 0),
    0,
  );
  return Math.round(total / topics.length);
}

function ProgramsPage() {
  const { t } = useTranslation();
  const { data, isLoading, isError, error, refetch } = useCurricula();
  const [importing, setImporting] = useState(false);

  const overall = useMemo(() => progressFor(data?.topics ?? []), [data]);

  return (
    <div className="space-y-6 px-5 pt-8">
      <PageHeader
        tone="sky"
        icon={<GraduationCap className="h-5 w-5" />}
        title={t("programs.title")}
        subtitle={t("programs.subtitle")}
      />

      <Card className="card-soft space-y-3 rounded-3xl border-0 p-5">
        <div className="flex items-center justify-between">
          <div className="text-sm font-semibold">{t("programs.yourYear")}</div>
          <div className="text-2xl font-semibold tabular-nums">{overall}%</div>
        </div>
        <Progress value={overall} className="h-2.5" />
        <p className="text-xs text-muted-foreground">{t("programs.completed")}</p>
      </Card>

      {!importing && (
        <Button className="w-full rounded-2xl" onClick={() => setImporting(true)}>
          <Upload className="mr-2 h-4 w-4" /> {t("programs.import")}
        </Button>
      )}

      {importing && <ImportWizard onDone={() => setImporting(false)} />}

      {isLoading && <LoadingSkeleton rows={3} />}
      {isError && <ErrorState error={error} onRetry={() => refetch()} />}

      <div className="space-y-2 pb-4">
        {(data?.curricula ?? []).map((c) => {
          const topics = (data?.topics ?? []).filter((x) => x.curriculum_id === c.id);
          const pct = progressFor(topics);
          return (
            <Link
              key={c.id}
              to="/programs/$curriculumId"
              params={{ curriculumId: c.id }}
              className="block"
            >
              <Card className="card-soft flex items-center gap-3 rounded-3xl border-0 p-4">
                <div className="text-2xl">{c.emoji ?? "📘"}</div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="truncate text-sm font-semibold">{c.subject_name}</span>
                    <span className="text-sm font-semibold tabular-nums text-primary">{pct}%</span>
                  </div>
                  <Progress value={pct} className="mt-2 h-1.5" />
                  <div className="mt-1 text-xs text-muted-foreground">
                    {topics.length} {t("programs.topics")}
                  </div>
                </div>
                <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
              </Card>
            </Link>
          );
        })}
        {!isLoading && !(data?.curricula ?? []).length && !importing && (
          <p className="py-6 text-center text-sm text-muted-foreground">{t("programs.empty")}</p>
        )}
      </div>
    </div>
  );
}

/* ----------------------------- IMPORT WIZARD ----------------------------- */

function ImportWizard({ onDone }: { onDone: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const runParse = useServerFn(parseProgram);
  const runSave = useServerFn(saveProgram);
  const [text, setText] = useState("");
  const [attached, setAttached] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState<ParsedProgramDoc | null>(null);

  async function analyze() {
    if (text.trim().length < 20) return toast.error(t("programs.paste"));
    setBusy(true);
    try {
      const res = await runParse({ data: { text: text.trim() } });
      setDraft(res);
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function confirm() {
    if (!draft) return;
    setBusy(true);
    try {
      await runSave({ data: { program: draft, source: "import" } });
      await qc.invalidateQueries({ queryKey: ["curricula"] });
      toast.success(t("programs.saved"));
      onDone();
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  function patchUnit(ui: number, title: string) {
    if (!draft) return;
    const units = draft.units.map((u, i) => (i === ui ? { ...u, title } : u));
    setDraft({ ...draft, units });
  }
  function patchTopic(ui: number, ti: number, title: string) {
    if (!draft) return;
    const units = draft.units.map((u, i) =>
      i === ui ? { ...u, topics: u.topics.map((tp, j) => (j === ti ? { ...tp, title } : tp)) } : u,
    );
    setDraft({ ...draft, units });
  }
  function removeTopic(ui: number, ti: number) {
    if (!draft) return;
    const units = draft.units
      .map((u, i) => (i === ui ? { ...u, topics: u.topics.filter((_, j) => j !== ti) } : u))
      .filter((u) => u.topics.length > 0);
    setDraft({ ...draft, units });
  }
  function addTopic(ui: number) {
    if (!draft) return;
    const units = draft.units.map((u, i) =>
      i === ui
        ? { ...u, topics: [...u.topics, { title: "", subtopics: [], difficulty: "media" as const, estimatedMinutes: 90 }] }
        : u,
    );
    setDraft({ ...draft, units });
  }
  function addUnit() {
    if (!draft) return;
    setDraft({
      ...draft,
      units: [
        ...draft.units,
        { title: "", topics: [{ title: "", subtopics: [], difficulty: "media", estimatedMinutes: 90 }] },
      ],
    });
  }

  if (draft) {
    return (
      <Card className="card-soft space-y-4 rounded-3xl border-0 p-5">
        <div className="text-sm font-semibold">{t("programs.review")}</div>
        <div className="grid grid-cols-1 gap-2">
          <Input
            value={draft.subject}
            placeholder={t("programs.subject")}
            onChange={(e) => setDraft({ ...draft, subject: e.target.value })}
          />
          <div className="grid grid-cols-2 gap-2">
            <Input
              value={draft.teacher ?? ""}
              placeholder={t("programs.teacher")}
              onChange={(e) => setDraft({ ...draft, teacher: e.target.value })}
            />
            <Input
              value={draft.schoolYear ?? ""}
              placeholder={t("programs.schoolYear")}
              onChange={(e) => setDraft({ ...draft, schoolYear: e.target.value })}
            />
          </div>
        </div>

        <div className="space-y-4">
          {draft.units.map((u, ui) => (
            <div key={ui} className="rounded-2xl bg-muted/40 p-3">
              <Input
                value={u.title}
                placeholder={`${t("programs.unit")} ${ui + 1}`}
                className="mb-2 font-medium"
                onChange={(e) => patchUnit(ui, e.target.value)}
              />
              <div className="space-y-1.5">
                {u.topics.map((tp, ti) => (
                  <div key={ti} className="flex items-center gap-1.5">
                    <Input
                      value={tp.title}
                      className="h-9 text-sm"
                      onChange={(e) => patchTopic(ui, ti, e.target.value)}
                    />
                    <button
                      type="button"
                      aria-label={t("programs.remove")}
                      onClick={() => removeTopic(ui, ti)}
                      className="rounded-lg p-2 text-muted-foreground hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
              <Button variant="ghost" size="sm" className="mt-1" onClick={() => addTopic(ui)}>
                <Plus className="mr-1 h-3.5 w-3.5" /> {t("programs.addTopic")}
              </Button>
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={addUnit}>
            <Plus className="mr-1 h-3.5 w-3.5" /> {t("programs.addUnit")}
          </Button>
        </div>

        <div className="flex gap-2">
          <Button className="flex-1 rounded-2xl" disabled={busy} onClick={confirm}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t("programs.confirm")}
          </Button>
          <Button variant="ghost" onClick={() => setDraft(null)}>←</Button>
        </div>
      </Card>
    );
  }

  return (
    <Card className="card-soft space-y-3 rounded-3xl border-0 p-5">
      <div className="flex items-center gap-2 text-sm font-semibold">
        <GraduationCap className="h-4 w-4 text-primary" /> {t("programs.import")}
      </div>
      <p className="text-xs text-muted-foreground">{t("programs.importDesc")}</p>
      <div className="flex gap-2 rounded-2xl bg-muted/50 p-3 text-xs text-muted-foreground">
        <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        <span>{t("programs.classeviva")}</span>
      </div>
      <Textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={t("programs.paste")}
        className="min-h-40 rounded-2xl"
      />
      <MediaAttachButton
        onText={(txt, name) => {
          setText((prev) => (prev ? `${prev}\n\n${txt}` : txt));
          setAttached(name);
        }}
        attachedName={attached}
        onClear={() => setAttached(null)}
      />
      <div className="flex gap-2">
        <Button className="flex-1 rounded-2xl" disabled={busy} onClick={analyze}>
          {busy ? (
            <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> {t("programs.analyzing")}</>
          ) : (
            t("programs.analyze")
          )}
        </Button>
        <Button variant="ghost" onClick={onDone}>✕</Button>
      </div>
    </Card>
  );
}
